import { useEffect, useState } from "react";
import { api, getErrorMessage } from "../../api";
import { useAuth } from "../../AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

export default function Profile() {
  const { user } = useAuth();
  const [buyer, setBuyer] = useState(null);
  const [countries, setCountries] = useState([]);
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [street, setStreet] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [postal, setPostal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const b = r.data.buyer;
      setBuyer(b);
      setPhone(b?.phone || ""); setCompany(b?.company || "");
      setStreet(b?.street_address || ""); setAddress2(b?.address_line2 || "");
      setCity(b?.city || ""); setPostal(b?.postal_code || "");
    });
    api.get("/countries").then((r) => setCountries(r.data));
  }, []);

  const countryName = (id) => countries.find((c) => c.id === id)?.name || "—";

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/buyers/me", {
        phone, company,
        street_address: street, address_line2: address2, city, postal_code: postal,
      });
      setBuyer(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed"));
    } finally { setBusy(false); }
  };

  if (!buyer) return <div className="max-w-3xl mx-auto p-6 text-[#5C665A]" data-testid="profile-loading">Loading…</div>;
  const dirty = phone !== (buyer.phone || "") || company !== (buyer.company || "")
    || street !== (buyer.street_address || "") || address2 !== (buyer.address_line2 || "")
    || city !== (buyer.city || "") || postal !== (buyer.postal_code || "");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
      <div className="db-h mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="profile-title">Profile</h1>
        <p className="text-sm text-[#5C665A]">Update your phone or company. Email and country changes require contacting us.</p>
      </div>

      <div className="bg-white border border-[#E2E4E0] rounded-lg p-5" data-testid="profile-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name" value={`${buyer.first_name} ${buyer.last_name}`} />
          <Field label="Email" value={user?.email} />
          <Field label="Country" value={countryName(buyer.country_id)} />
          <Field label="VAT / Tax ID" value={buyer.vat_id} />
          <Field label="Import licence" value={buyer.import_license ? (buyer.import_license_number || "yes") : "no"} />
          <Field label="Personal discount" value={`${buyer.personal_discount_percent || 0}%`} />
        </div>
        <div className="border-t border-[#E2E4E0] mt-4 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#5C665A]">Phone</label>
            <Input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Company</label>
            <Input data-testid="profile-company" value={company} onChange={(e) => setCompany(e.target.value)} className="h-9" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[#5C665A]">Street address</label>
            <Input data-testid="profile-street" value={street} onChange={(e) => setStreet(e.target.value)} className="h-9" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[#5C665A]">Address line 2 (optional)</label>
            <Input data-testid="profile-address2" value={address2} onChange={(e) => setAddress2(e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Postal code</label>
            <Input data-testid="profile-postal" value={postal} onChange={(e) => setPostal(e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">City</label>
            <Input data-testid="profile-city" value={city} onChange={(e) => setCity(e.target.value)} className="h-9" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="profile-save" disabled={!dirty || busy} onClick={save} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50">Save</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#5C665A]">{label}</div>
      <div className="text-sm text-[#1A1D1A]">{value || "—"}</div>
    </div>
  );
}
